import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import './MarqueeText.css';

/**
 * MarqueeText - Beautiful scrolling text component
 * When text overflows its container, it smoothly scrolls on hover
 */
const MarqueeText = ({
  children,
  className = '',
  speed = 3000, // duration in milliseconds
  direction = 'left',
  style = {},
  onClick,
  ...props
}) => {
  const containerRef = useRef(null);
  const textRef = useRef(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const [scrollAmount, setScrollAmount] = useState(0);

  // Check if text overflows container
  const checkOverflow = () => {
    if (containerRef.current && textRef.current) {
      const containerWidth = containerRef.current.offsetWidth;
      const textWidth = textRef.current.scrollWidth;
      const overflow = textWidth > containerWidth;
      setIsOverflowing(overflow);
      
      if (overflow) {
        const amount = textWidth - containerWidth + 20; // extra 20px for padding
        setScrollAmount(amount);
      }
    }
  };

  // Handle mouse enter
  const handleMouseEnter = () => {
    if (isOverflowing) {
      setShouldAnimate(true);
    }
  };

  // Handle mouse leave
  const handleMouseLeave = () => {
    setShouldAnimate(false);
  };

  // Setup overflow detection
  useEffect(() => {
    checkOverflow();
    
    const handleResize = () => checkOverflow();
    window.addEventListener('resize', handleResize);
    
    return () => window.removeEventListener('resize', handleResize);
  }, [children]);

  const containerClassName = [
    'marquee-text-container',
    className,
    isOverflowing ? 'marquee-text-overflow' : '',
    onClick ? 'marquee-text-clickable' : ''
  ].filter(Boolean).join(' ');

  const textStyle = {
    transform: shouldAnimate 
      ? `translateX(-${scrollAmount}px)`
      : 'translateX(0)',
    transition: shouldAnimate 
      ? `transform ${speed}ms ease-in-out`
      : 'transform 0.3s ease-out',
    ...style
  };

  return (
    <div
      ref={containerRef}
      className={containerClassName}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
      {...props}
    >
      <span
        ref={textRef}
        className="marquee-text-content"
        style={textStyle}
      >
        {children}
      </span>
      
      {/* Fade effects */}
      {isOverflowing && (
        <>
          <div className="marquee-text-fade marquee-text-fade-left" />
          <div className="marquee-text-fade marquee-text-fade-right" />
        </>
      )}
    </div>
  );
};

MarqueeText.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  speed: PropTypes.number, // duration in milliseconds
  direction: PropTypes.oneOf(['left', 'right']),
  style: PropTypes.object,
  onClick: PropTypes.func
};

export default MarqueeText;
